import { ATableHeader } from '../../organisms/ATable';

// --- Types ---

export interface BatchReportInfo {
    id?: string;
    identification?: string;
    provider?: string;
    createdAt?: string;
    status?: string;
    documents?: number;
    errors?: number;
    success?: number;
    sending?: number;
    statusFormatted?: string;
    reportType?: string;
    batchReportId?: string;
    canUpdateStatus?: boolean;
    mini_uuid: string;
}

export interface BatchReportItem {
    document: string;
    createdAt?: string;
    provider?: string;
    status?: string;
    statusFormatted?: string;
    errorMessage?: string | null;
    [key: string]: any;
}

export interface ReportItem {
    document: string;
    createdAt?: string;
    updatedAt?: string;
    status?: string;
    statusFormatted?: string;
    errorMessage?: string | null;
    reportType?: string;
    reportTypeId?: string;
    batchReportId?: string;
    attempts?: number;
    maxAttempts?: number;
    events?: Array<{
        eventId: string;
        eventName: string;
        occurredAt: string;
    }>;
}

// --- Constants ---

const EVENT_COLOR_MAP = new Map<string, string>([
    ['PENDING_REPORT_CREATED', 'bg-yellow-500'],
    ['REPORT_PROCESSING', 'bg-yellow-500'],
    ['REPORT_ERROR', 'bg-red-500'],
    ['REPORT_FAILED', 'bg-red-500'],
    ['REPORT_COMPLETED', 'bg-blue-500'],
]);

// --- Helpers ---

export const getEventColor = (eventName: string): string =>
    EVENT_COLOR_MAP.get(eventName) || 'bg-gray-500';

export const getCssColorByStatus = (status?: string): string => {
    if (!status) return 'bg-gray-800';
    const cssClazz: Record<string, string> = {
        PENDING: 'bg-gray-500',
        ERROR: 'bg-red-500',
        COMPLETED: 'bg-green-500',
    };
    return cssClazz[status.toUpperCase()] || 'bg-gray-500';
};

export const getCssColorBy = (status?: string): string => {
    if (status === 'SUCCESS') return 'bg-green-500';
    if (status === 'ERROR') return 'bg-red-500';
    if (status === 'COMPLETED') return 'bg-blue-500';
    return 'bg-yellow-500';
};

export const getContentByStatus = (status?: string, errorMessage?: string | null): string => {
    if (status === 'ERROR' && errorMessage) return `Erro: ${errorMessage}`;
    if (status === 'SUCCESS') return 'Consulta realizada com sucesso';
    if (status === 'ERROR') return 'Erro ao consultar';
    if (status === 'COMPLETED') return 'Consulta finalizada';
    return 'Consulta pendente';
};

// --- Export logic ---

export const exportPageToCsv = (rows: any[], headers: ATableHeader[], id: string, currentPage: number): void => {
    if (!rows.length || !headers.length) return;
    const sanitize = (val: string): string => /^[=+\-@\t\r]/.test(val) ? `'${val}` : val;
    const csvHeaders = headers.map(h => sanitize(h.title)).join(';');
    const csvRows = rows.map(row =>
        headers.map(h => {
            const val = (row as any)[h.field || h.id];
            if (val === null || val === undefined) return '';
            if (typeof val === 'object') return sanitize(JSON.stringify(val));
            return sanitize(String(val));
        }).join(';')
    ).join('\n');
    const csv = `${csvHeaders}\n${csvRows}`;
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `batch-report-${id}-page-${currentPage + 1}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
};

export const exportFullBatch = async (fetcher: any, id: string): Promise<void> => {
    const response = await fetcher.get(`/batch-reports/${id}/download`);
    if (!response.ok) throw new Error('Download failed');
    const contentType = response.headers.get('content-type') || '';
    if (contentType.includes('application/json')) {
        const json = await response.json();
        if (json.downloadUrl) {
            window.open(json.downloadUrl, '_blank');
            return;
        }
        throw new Error('Unexpected JSON response');
    }
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `batch-report-${id}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
};
